import { contactsApi } from '@/features/Contact/api/contactsApi';
import type { UpdateContactData } from '@/features/Contact/types/contact.types';
import {
    saveMyRegisterContactAddresses,
    type MyRegisterContactAddressSavePayload,
} from '../api/myRegisterApi';
import type { EntityDirectorAddressSaveRefs } from './saveMyRegisterEntityDirector';
import {
    MY_REGISTER_DIRECTOR_POSITION_CD,
    mergeAddressIntoContactUpdate,
} from './saveMyRegisterEntityDirector';
import {
    normalizeRegisterAddressListForSave,
    normalizeRegisterAddressRowForSave,
    normalizeRegisterBranchAddressListForSave,
    normalizeRegisterBranchAddressRowForSave,
    isRegisterAddressGuidLike,
} from './registerAddressSaveNormalize';
import {
    shouldUseRegisterCorporateBranchSave,
    resolveRegisterContactCategoryCd,
} from '../constants/myRegisterPositionCd';

function strField(v: unknown): string {
    return String(v ?? '').trim();
}

function hasAddressRowContent(row: Record<string, unknown> | null | undefined): boolean {
    if (!row) return false;
    const keys = [
        'street01', 'street02', 'street03',
        'addressLine1', 'addressLine2', 'addressLine3',
        'post_code', 'postcode', 'postcodeCode', 'postalCode',
        'city', 'cityCode', 'state', 'stateCode', 'country', 'countryCode',
    ];
    return keys.some((k) => strField(row[k]).length > 0);
}

function cloneAddressRows(
    rows: unknown[] | null | undefined,
    useBranchCodes: boolean,
): Record<string, unknown>[] {
    return useBranchCodes
        ? normalizeRegisterBranchAddressListForSave(rows)
        : normalizeRegisterAddressListForSave(rows);
}

function collectResidentialRowsFromRefs(
    refs: EntityDirectorAddressSaveRefs,
    entityCd: string,
    positionName: string,
    useBranchCodes: boolean,
): Record<string, unknown>[] {
    if (refs.useBranchAddresses) {
        const fromForm = refs.getResidentialBranchListForSave?.();
        if (Array.isArray(fromForm)) {
            return cloneAddressRows(fromForm, useBranchCodes);
        }
        if (fromForm && typeof fromForm === 'object') {
            return cloneAddressRows([fromForm], useBranchCodes);
        }

        const contactInfo = refs.contactInfoData as Record<string, unknown> | null;
        const fromContactInfo = contactInfo?.residential_address;
        if (Array.isArray(fromContactInfo) && fromContactInfo.length > 0) {
            return cloneAddressRows(fromContactInfo as unknown[], useBranchCodes);
        }
    }

    const addressData = refs.addressData as Record<string, unknown> | null;
    const localAddress = addressData?.localAddress as Record<string, unknown> | undefined;
    if (localAddress && hasAddressRowContent(localAddress)) {
        return [localAddressToSaveRow(localAddress, entityCd, positionName, useBranchCodes)];
    }

    return [];
}

function collectServiceBusinessRowsFromRefs(
    refs: EntityDirectorAddressSaveRefs,
    useBranchCodes: boolean,
): { service: Record<string, unknown>[]; business: Record<string, unknown>[] } {
    const branchLists = refs.getBranchAddressListsForSave?.();
    if (branchLists) {
        return {
            service: cloneAddressRows(branchLists.serviceAddressList, useBranchCodes),
            business: cloneAddressRows(branchLists.businessAddressList, useBranchCodes),
        };
    }

    const contactInfo = refs.contactInfoData as Record<string, unknown> | null;
    return {
        service: cloneAddressRows(contactInfo?.serviceAddressList as unknown[], useBranchCodes),
        business: cloneAddressRows(contactInfo?.businessAddressList as unknown[], useBranchCodes),
    };
}

function buildLocalAddressFromRegisterJson(
    jsonData: Record<string, unknown>,
    prefix: string,
): Record<string, string> | undefined {
    const p = prefix ? `${prefix}_` : '';
    const line1 = strField(jsonData[`${p}STREET01`] ?? jsonData[`${p}ADDRESS_LINE_1`]);
    const postCode = strField(
        jsonData[`${p}POST_CODE`]
        ?? jsonData[`${p}POSTCODE`]
        ?? jsonData[`${p}POSTAL_CODE`],
    );
    if (!line1 && !postCode) return undefined;
    return {
        addressLine1: line1,
        addressLine2: strField(jsonData[`${p}STREET02`] ?? jsonData[`${p}ADDRESS_LINE_2`]),
        addressLine3: strField(jsonData[`${p}STREET03`] ?? jsonData[`${p}ADDRESS_LINE_3`]),
        postcode: postCode,
        post_code: postCode,
        city: strField(jsonData[`${p}CITY`] ?? jsonData[`${p}CITY_TEXT`]),
        state: strField(jsonData[`${p}STATE`] ?? jsonData[`${p}STATE_TEXT`]),
        country: strField(jsonData[`${p}COUNTRY`] ?? jsonData[`${p}COUNTRY_CODE`]),
        postcodeCode: strField(jsonData[`${p}POSTCODE_CODE`]),
        cityCode: strField(jsonData[`${p}CITY_CODE`]),
        stateCode: strField(jsonData[`${p}STATE_CODE`]),
        countryCode: strField(jsonData[`${p}COUNTRY_CODE`] ?? jsonData[`${p}COUNTRY`]),
    };
}

function localAddressToSaveRow(
    local: Record<string, unknown>,
    entityCd: string,
    positionName: string,
    useBranchCodes: boolean,
): Record<string, unknown> {
    const str = (v: unknown) => String(v ?? '').trim();
    const pickPostCode = (...values: unknown[]): string => {
        for (const v of values) {
            const s = str(v);
            if (s && !isRegisterAddressGuidLike(s)) return s;
        }
        return '';
    };
    const row = {
        street01: str(local.street01 || local.addressLine1),
        street02: str(local.street02 || local.addressLine2),
        street03: str(local.street03 || local.addressLine3),
        post_code: pickPostCode(local.post_code, local.postalCode, local.POST_CODE, local.postcode),
        city: str(local.city || local.cityCode || local.CITY),
        state: str(local.state || local.stateCode || local.STATE),
        country: str(local.country || local.countryCode || local.COUNTRY),
        postcodeCode: str(local.postcodeCode || local.POSTCODE_CODE),
        cityCode: str(local.cityCode || local.CITY_CODE),
        stateCode: str(local.stateCode || local.STATE_CODE),
        countryCode: str(local.countryCode || local.COUNTRY),
        entity_cd: entityCd,
        position: positionName,
        branch_id: local.branch_id ?? local.BRANCH_ID ?? local.address_id ?? local.ADDRESS_ID,
        address_id: local.address_id ?? local.ADDRESS_ID ?? local.branch_id ?? local.BRANCH_ID,
    };
    return useBranchCodes
        ? normalizeRegisterBranchAddressRowForSave(row)
        : normalizeRegisterAddressRowForSave(row);
}

/**
 * Register 주소 UI / jsonData → Register API 저장 payload
 */
export function buildRegisterAddressSavePayload(opts: {
    entityCd: string;
    positionCd?: string;
    positionName?: string;
    categoryCd?: string;
    refs?: EntityDirectorAddressSaveRefs | null;
    jsonData?: Record<string, unknown>;
}): MyRegisterContactAddressSavePayload | null {
    const entityCd = String(opts.entityCd || '').trim();
    const positionCd = String(opts.positionCd || MY_REGISTER_DIRECTOR_POSITION_CD).trim();
    const positionName = String(opts.positionName || 'Director').trim();
    const categoryCd = resolveRegisterContactCategoryCd(opts.jsonData, opts.categoryCd);
    const useBranchCodes = shouldUseRegisterCorporateBranchSave(categoryCd, positionCd);
    if (!entityCd || !positionCd) return null;

    const payload: MyRegisterContactAddressSavePayload = {
        positionCd,
        positionName,
        categoryCd,
        residential: [],
        service: [],
        business: [],
    };

    const refs = opts.refs;
    const jsonData = opts.jsonData;

    if (refs) {
        payload.residential = collectResidentialRowsFromRefs(refs, entityCd, positionName, useBranchCodes);
        const branchLists = collectServiceBusinessRowsFromRefs(refs, useBranchCodes);
        payload.service = branchLists.service;
        payload.business = branchLists.business;
    } else if (jsonData) {
        const local = buildLocalAddressFromRegisterJson(jsonData, '');
        const service = buildLocalAddressFromRegisterJson(jsonData, 'SERVICE');
        const business = buildLocalAddressFromRegisterJson(jsonData, 'BUS');
        if (local) {
            payload.residential = [localAddressToSaveRow(local, entityCd, positionName, useBranchCodes)];
        }
        if (service) {
            payload.service = [localAddressToSaveRow(service, entityCd, positionName, useBranchCodes)];
        }
        if (business) {
            payload.business = [localAddressToSaveRow(business, entityCd, positionName, useBranchCodes)];
        }
    }

    const hasAny =
        (payload.residential?.length ?? 0) > 0
        || (payload.service?.length ?? 0) > 0
        || (payload.business?.length ?? 0) > 0
        || (payload.other?.length ?? 0) > 0;

    if (!hasAny && jsonData) {
        const local = buildLocalAddressFromRegisterJson(jsonData, '');
        const service = buildLocalAddressFromRegisterJson(jsonData, 'SERVICE');
        const business = buildLocalAddressFromRegisterJson(jsonData, 'BUS');
        if (local) {
            payload.residential = [localAddressToSaveRow(local, entityCd, positionName, useBranchCodes)];
        }
        if (service) {
            payload.service = [localAddressToSaveRow(service, entityCd, positionName, useBranchCodes)];
        }
        if (business) {
            payload.business = [localAddressToSaveRow(business, entityCd, positionName, useBranchCodes)];
        }
    }

    const hasAnyAfterFallback =
        (payload.residential?.length ?? 0) > 0
        || (payload.service?.length ?? 0) > 0
        || (payload.business?.length ?? 0) > 0
        || (payload.other?.length ?? 0) > 0;

    return hasAnyAfterFallback ? payload : null;
}

function buildRegisterAddressSavePayloadFromMerged(
    merged: Record<string, unknown>,
    entityCd: string,
    positionCd: string,
    positionName: string,
    categoryCd?: string,
): MyRegisterContactAddressSavePayload | null {
    const useBranchCodes = shouldUseRegisterCorporateBranchSave(categoryCd, positionCd);
    const payload: MyRegisterContactAddressSavePayload = {
        positionCd,
        positionName,
        categoryCd: resolveRegisterContactCategoryCd(undefined, categoryCd),
        residential: [],
        service: [],
        business: [],
    };

    const residentialList = merged.residentialAddressList;
    if (Array.isArray(residentialList) && residentialList.length > 0) {
        payload.residential = cloneAddressRows(residentialList, useBranchCodes);
    } else {
        const local = merged.localAddress as Record<string, unknown> | undefined;
        if (local && hasAddressRowContent(local)) {
            payload.residential = [localAddressToSaveRow(local, entityCd, positionName, useBranchCodes)];
        }
    }

    if (Array.isArray(merged.serviceAddressList)) {
        payload.service = cloneAddressRows(merged.serviceAddressList as unknown[], useBranchCodes);
    }
    if (Array.isArray(merged.businessAddressList)) {
        payload.business = cloneAddressRows(merged.businessAddressList as unknown[], useBranchCodes);
    }

    const hasAny =
        (payload.residential?.length ?? 0) > 0
        || (payload.service?.length ?? 0) > 0
        || (payload.business?.length ?? 0) > 0
        || (payload.other?.length ?? 0) > 0;

    return hasAny ? payload : null;
}

/** Register 저장 시 Contact PUT에 실어내면 안 되는 주소·MAP 필드 제거 */
export function stripRegisterAddressFieldsFromContactUpdate(
    base: UpdateContactData & Record<string, unknown>,
): UpdateContactData & Record<string, unknown> {
    const update = { ...base };
    const removeKeys = [
        'entityCd',
        'branchEntityCd',
        'BRANCH_ENTITY_CD',
        'positionCd',
        'positionId',
        'positionName',
        'position',
        'localAddress',
        'foreignAddress',
        'altLocalAddress',
        'registeredAddress',
        'residentialAddressList',
        'serviceAddressList',
        'businessAddressList',
        'useBranchAddresses',
        'USE_BRANCH_ADDRESSES',
        'skipResidentialJsonDataWrite',
        'skipServiceBusinessDedicatedProfileColumnSync',
    ];
    for (const key of removeKeys) {
        delete update[key];
    }
    update.skipMyBranchAddressSave = true;
    update.skipResidentialJsonDataWrite = true;
    return update;
}

function resolveRegisterAddressSavePayload(opts: {
    entityCd: string;
    positionCd?: string;
    positionName?: string;
    categoryCd?: string;
    refs?: EntityDirectorAddressSaveRefs | null;
    jsonData?: Record<string, unknown>;
}): MyRegisterContactAddressSavePayload | null {
    const entityCd = String(opts.entityCd || '').trim();
    const positionCd = String(opts.positionCd || MY_REGISTER_DIRECTOR_POSITION_CD).trim();
    const positionName = String(opts.positionName || 'Director').trim();
    const categoryCd = resolveRegisterContactCategoryCd(opts.jsonData, opts.categoryCd);

    const direct = buildRegisterAddressSavePayload(opts);
    if (direct) return direct;

    const merged = mergeAddressIntoContactUpdate(
        {},
        entityCd,
        opts.refs ?? null,
        opts.jsonData,
        { positionCd, positionName },
    );
    return buildRegisterAddressSavePayloadFromMerged(
        merged,
        entityCd,
        positionCd,
        positionName,
        categoryCd,
    );
}

/**
 * MY Register — L_CONTACT_ADDRESS + L_CONTACT_BRANCH_MAP 저장 (Contact API와 분리)
 */
export async function saveMyRegisterRoleContactAddresses(opts: {
    entityCd: string;
    contactCd: string;
    positionCd?: string;
    positionName?: string;
    categoryCd?: string;
    refs?: EntityDirectorAddressSaveRefs | null;
    jsonData?: Record<string, unknown>;
}): Promise<void> {
    const entityCd = String(opts.entityCd || '').trim();
    const contactCd = String(opts.contactCd || '').trim();
    if (!entityCd || !contactCd) return;

    const payload = resolveRegisterAddressSavePayload(opts);
    if (!payload) return;

    await saveMyRegisterContactAddresses(entityCd, contactCd, payload);
}

/**
 * Register 저장 — 주소(Register API) + 프로필(Contact API) 분리 호출
 */
export async function saveMyRegisterContactProfileAndAddresses(opts: {
    entityCd: string;
    contactCd: string;
    positionCd: string;
    positionName?: string;
    categoryCd?: string;
    contactUpdate: UpdateContactData & Record<string, unknown>;
    refs?: EntityDirectorAddressSaveRefs | null;
    jsonData?: Record<string, unknown>;
}): Promise<void> {
    await saveMyRegisterRoleContactAddresses({
        entityCd: opts.entityCd,
        contactCd: opts.contactCd,
        positionCd: opts.positionCd,
        positionName: opts.positionName,
        categoryCd: opts.categoryCd ?? resolveRegisterContactCategoryCd(opts.jsonData),
        refs: opts.refs,
        jsonData: opts.jsonData,
    });
    const profileOnly = stripRegisterAddressFieldsFromContactUpdate(opts.contactUpdate);
    await contactsApi.updateContact(opts.contactCd, profileOnly);
}
